import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import styles from './ProfileSettings.module.css';

export default function ProfileSettings({ userProfile, loading, error }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nickname: '',
    company: '',
    job_title: '',
    phone_number: ''
  });
  const [originalData, setOriginalData] = useState({});
  const [changedFields, setChangedFields] = useState(new Set());
  const [saveLoading, setSaveLoading] = useState(false);

  // Initialize form data when userProfile changes
  useEffect(() => {
    if (userProfile) {
      console.log('ProfileSettings: userProfile data received:', userProfile);
      console.log('ProfileSettings: tenant_role:', userProfile.tenant_role);
      const initialData = {
        nickname: userProfile.nickname || '',
        company: userProfile.company || '',
        job_title: userProfile.job_title || '',
        phone_number: userProfile.phone_number || ''
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setChangedFields(new Set());
    }
  }, [userProfile]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Check if field has changed from original
    if (value !== originalData[field]) {
      setChangedFields(prev => new Set([...prev, field]));
    } else {
      setChangedFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(field);
        return newSet;
      });
    }
  };

  const hasChanges = changedFields.size > 0;

  const getRoleColor = (role) => {
    const roleColors = {
      'owner': '#dc2626',    // Red
      'admin': '#16a34a',    // Green
      'member': '#2563eb',   // Blue
      'viewer': '#ca8a04',   // Yellow
      'invite': '#ea580c'    // Orange
    };
    return roleColors[role] || '#6b7280'; // Default gray for unknown roles
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      // TODO: Implement API call to save profile changes
      console.log('Saving profile changes:', formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update original data to reflect saved state
      setOriginalData(formData);
      setChangedFields(new Set());
      setIsEditing(false);
      
      // Show success message
      console.log('Profile saved successfully');
    } catch (error) {
      console.error('Failed to save profile:', error);
      // Show error message
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    setFormData(originalData);
    setChangedFields(new Set());
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p>{error}</p>
        <button 
          className={styles.retryButton}
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className={styles.errorContainer}>
        <p>No profile information available.</p>
      </div>
    );
  }

  return (
    <div className={styles.profileSettings}>
      <div className={styles.header}>
        <div>
          <h1>Profile Settings</h1>
          <p className={styles.subtitle}>Manage your account information and preferences</p>
        </div>
        {!isEditing && (
          <button 
            className={styles.editButton}
            onClick={handleEdit}
          >
            Edit Profile
          </button>
        )}
      </div>

      <div className={styles.settingsSection}>
        <h2>Personal Information</h2>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Email Address</label>
          <div className={styles.fieldValue}>
            <div className={styles.emailRow}>
              <span className={styles.readOnlyValue}>{userProfile.email}</span>
              {userProfile.tenant_role && (
                <span 
                  className={styles.roleBanner}
                  style={{ backgroundColor: getRoleColor(userProfile.tenant_role) }}
                >
                  {userProfile.tenant_role.toUpperCase()}
                </span>
              )}
              {userProfile.email_verified ? (
                <div className={styles.emailVerified}>
                  <CheckCircle size={16} color="#16a34a" />
                  <span>Verified</span>
                </div>
              ) : (
                <div className={styles.emailUnverified}>
                  <AlertTriangle size={16} color="#ca8a04" />
                  <span>Email not verified</span>
                </div>
              )}
            </div>
            <span className={styles.fieldNote}>Email address cannot be changed for security reasons</span>
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Display Name</label>
          <div className={styles.fieldValue}>
            {isEditing ? (
              <input
                type="text"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                className={`${styles.inputField} ${changedFields.has('nickname') ? styles.changed : ''}`}
                placeholder="Enter your display name"
              />
            ) : (
              <span className={styles.readOnlyValue}>{userProfile.nickname || 'Not set'}</span>
            )}
            {changedFields.has('nickname') && (
              <div className={styles.changeIndicator}>
                <span>✏️ Modified - click Save to update</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Authentication Method</label>
          <div className={styles.fieldValue}>
            <span className={styles.readOnlyValue}>{userProfile.oauth_provider || 'Email'}</span>
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Phone Number</label>
          <div className={styles.fieldValue}>
            {isEditing ? (
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                className={`${styles.inputField} ${changedFields.has('phone_number') ? styles.changed : ''}`}
                placeholder="Enter your phone number"
              />
            ) : (
              <span className={styles.readOnlyValue}>{userProfile.phone_number || 'Not set'}</span>
            )}
            {changedFields.has('phone_number') && (
              <div className={styles.changeIndicator}>
                <span>✏️ Modified - click Save to update</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.settingsSection}>
        <h2>Work Information</h2>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Company</label>
          <div className={styles.fieldValue}>
            {isEditing ? (
              <input
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                className={`${styles.inputField} ${changedFields.has('company') ? styles.changed : ''}`}
                placeholder="Enter company name"
              />
            ) : (
              <span className={styles.readOnlyValue}>{userProfile.company || 'Not set'}</span>
            )}
            {changedFields.has('company') && (
              <div className={styles.changeIndicator}>
                <span>✏️ Modified - click Save to update</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Job Title</label>
          <div className={styles.fieldValue}>
            {isEditing ? (
              <input
                type="text"
                value={formData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
                className={`${styles.inputField} ${changedFields.has('job_title') ? styles.changed : ''}`}
                placeholder="Enter job title"
              />
            ) : (
              <span className={styles.readOnlyValue}>{userProfile.job_title || 'Not set'}</span>
            )}
            {changedFields.has('job_title') && (
              <div className={styles.changeIndicator}>
                <span>✏️ Modified - click Save to update</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {userProfile.teams && userProfile.teams.length > 0 && (
        <div className={styles.settingsSection}>
          <h2>Team Memberships</h2>
          {userProfile.teams.map((team, index) => (
            <div key={team.id} className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>{team.name}</label>
              <div className={styles.fieldValue}>
                <span className={styles.readOnlyValue}>{team.role} • {team.description || 'No description'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save/Cancel Buttons */}
      {isEditing && (
        <div className={styles.actionButtons}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={!hasChanges || saveLoading}
          >
            {saveLoading ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={saveLoading}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
